"use client";

import { useEffect, useRef, useState, type SyntheticEvent } from "react";
import Image from "next/image";
import Link from "next/link";
import type { Route } from "next";
import { Play, X } from "lucide-react";

import { event } from "@/lib/gtag";
import {
  buildVideoAnalyticsParams,
  createVideoPlaybackId,
  VIDEO_PROGRESS_MILESTONES,
} from "@/lib/video/analytics";
import type { VideoContentType } from "@/lib/video/published-videos";

const VIDEO_PREVIEW_START_EVENT = "eclat:video-preview-start";
const HOVER_PREVIEW_DELAY_MS = 180;

type PreviewMode = "hover" | "pinned" | null;

export type VideoCardData = {
  slug: string;
  subject?: string | null;
  thumbnailTitle: string;
  topicLabel?: string | null;
  fullTitle: string;
  analyticsTitle?: string;
  posterUrl: string;
  videoUrl: string;
  duration: string;
  durationSeconds?: number;
  contentType: VideoContentType;
  href: string;
  resultRank: number;
};

type MotifKind =
  | "graph"
  | "probability"
  | "wave"
  | "mechanics"
  | "electricity"
  | "atom"
  | "molecule"
  | "cell"
  | "genetics";

function getMotifKind(video: VideoCardData): MotifKind {
  const title = (video.analyticsTitle || video.fullTitle).toLowerCase();
  if (
    /probability|distribution|binomial|normal|hypothesis|regression/.test(title)
  ) {
    return "probability";
  }
  if (/wave|oscillation|interference|polarisation/.test(title)) return "wave";
  if (
    /current|potential|electric|e\.m\.f|circuit|coil|resistance/.test(title)
  ) {
    return "electricity";
  }
  if (/force|moment|motion|collision|impulse|orbit|field/.test(title)) {
    return "mechanics";
  }
  if (/atom|electron|ionisation|quantum|photoelectric|nuclear/.test(title)) {
    return "atom";
  }
  if (/bond|molecule|acid|base|equilibrium|energetic|titration/.test(title)) {
    return "molecule";
  }
  if (/gene|inherit|cross|dna|rna|selection|speciation/.test(title)) {
    return "genetics";
  }
  if (video.subject?.includes("biology")) return "cell";
  return "graph";
}

function RecognitionSketch({ kind }: { kind: MotifKind }) {
  const shared = {
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 4,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
  };

  return (
    <svg viewBox="0 0 160 120" className="h-full w-full" aria-hidden="true">
      {kind === "graph" ? (
        <>
          <path d="M22 16v82h116" {...shared} />
          <path
            d="M31 88c18-2 22-53 44-53 17 0 20 45 39 45 10 0 17-12 25-28"
            {...shared}
          />
          <path d="m132 45 9 5-4 10" {...shared} />
        </>
      ) : null}
      {kind === "probability" ? (
        <>
          <path d="M20 98h120" {...shared} />
          <path d="M31 97c20-1 24-63 49-63s29 62 49 63" {...shared} />
          <path d="M80 34v63" {...shared} strokeDasharray="7 8" />
        </>
      ) : null}
      {kind === "wave" ? (
        <>
          <path d="M12 61c15-42 29-42 44 0s29 42 44 0 29-42 48 0" {...shared} />
          <path
            d="M12 98h136M80 18v86"
            {...shared}
            strokeWidth="2.5"
            opacity="0.45"
          />
        </>
      ) : null}
      {kind === "mechanics" ? (
        <>
          <circle cx="58" cy="70" r="22" {...shared} />
          <path
            d="M80 70h54m-12-12 12 12-12 12M58 48V18m-10 12 10-12 10 12"
            {...shared}
          />
          <path d="M24 98h112" {...shared} />
        </>
      ) : null}
      {kind === "electricity" ? (
        <>
          <path d="M22 36h32l10-16 18 32 18-32 10 16h28v55H22Z" {...shared} />
          <path d="M42 91v14m76-14v14M54 105h52" {...shared} />
          <path d="M80 67v23m-10-12 10 12 10-12" {...shared} />
        </>
      ) : null}
      {kind === "atom" ? (
        <>
          <circle cx="80" cy="60" r="8" fill="currentColor" />
          <ellipse cx="80" cy="60" rx="56" ry="22" {...shared} />
          <ellipse
            cx="80"
            cy="60"
            rx="56"
            ry="22"
            transform="rotate(60 80 60)"
            {...shared}
          />
          <ellipse
            cx="80"
            cy="60"
            rx="56"
            ry="22"
            transform="rotate(120 80 60)"
            {...shared}
          />
        </>
      ) : null}
      {kind === "molecule" ? (
        <>
          <circle cx="80" cy="58" r="18" {...shared} />
          <circle cx="31" cy="30" r="12" {...shared} />
          <circle cx="129" cy="30" r="12" {...shared} />
          <circle cx="44" cy="100" r="12" {...shared} />
          <circle cx="116" cy="100" r="12" {...shared} />
          <path
            d="m64 48-22-12m54 12 22-12M67 72 52 92m41-20 15 20"
            {...shared}
          />
        </>
      ) : null}
      {kind === "cell" ? (
        <>
          <path
            d="M28 72c-8-27 16-51 46-51 27 0 62 14 59 47-2 31-36 37-61 31-20-5-38-8-44-27Z"
            {...shared}
          />
          <circle cx="81" cy="61" r="20" {...shared} />
          <path
            d="M39 68c10-8 17-8 27-1m42-24c7 5 10 11 9 18"
            {...shared}
            strokeWidth="3"
          />
        </>
      ) : null}
      {kind === "genetics" ? (
        <>
          <path
            d="M42 16c0 29 76 59 76 88M118 16c0 29-76 59-76 88"
            {...shared}
          />
          <path
            d="M53 29h54M48 48h64M48 72h64m-59 20h54"
            {...shared}
            strokeWidth="3"
          />
        </>
      ) : null}
    </svg>
  );
}

type Labels = {
  preview: string;
  sound: string;
  close: string;
  open: string;
  play: string;
  topic: string;
};
export default function VideoCard({
  video,
  labels,
  locale = "en",
}: {
  video: VideoCardData;
  labels?: Labels;
  locale?: "en" | "zh-Hans";
}) {
  const label = (key: keyof Labels, fallback: string) =>
    labels ? labels[key].replace("{title}", video.fullTitle) : fallback;
  const [previewMode, setPreviewMode] = useState<PreviewMode>(null);
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const previewActivationRef = useRef<Exclude<PreviewMode, null>>("pinned");
  const playbackIdRef = useRef("");
  if (!playbackIdRef.current) playbackIdRef.current = createVideoPlaybackId();
  const hasViewedRef = useRef(false);
  const hasStartedRef = useRef(false);
  const hasCompletedRef = useRef(false);
  const hasErroredRef = useRef(false);
  const progressMilestonesRef = useRef<Set<number>>(new Set());
  const maxProgressRef = useRef(0);
  const watchedSecondsRef = useRef(0);
  const lastMediaTimeRef = useRef<number | null>(null);
  const nativeSeekFromRef = useRef<number | null>(null);

  const buildPreviewParams = (progressPercent?: number) => ({
    ...buildVideoAnalyticsParams(
      {
        videoSlug: video.slug,
        videoTitle: video.analyticsTitle || video.fullTitle,
        videoSubject: video.subject,
        videoContentType: video.contentType,
        videoDurationSeconds: video.durationSeconds,
        sourcePage: "/videos",
        sourceSurface: "video_card",
        playerSurface: "library_preview",
        resultRank: video.resultRank,
      },
      playbackIdRef.current,
      {
        elapsedSeconds: Math.round(watchedSecondsRef.current),
        maxProgressPercent: Math.round(maxProgressRef.current),
        progressPercent,
      }
    ),
    preview_activation: previewActivationRef.current,
  });

  const resetPreviewAttempt = () => {
    playbackIdRef.current = createVideoPlaybackId();
    hasViewedRef.current = false;
    hasStartedRef.current = false;
    hasCompletedRef.current = false;
    hasErroredRef.current = false;
    progressMilestonesRef.current.clear();
    maxProgressRef.current = 0;
    watchedSecondsRef.current = 0;
    lastMediaTimeRef.current = 0;
  };

  useEffect(() => {
    const stopOtherPreview = (event: Event) => {
      if ((event as CustomEvent<string>).detail !== video.slug) {
        setPreviewMode(null);
      }
    };

    window.addEventListener(VIDEO_PREVIEW_START_EVENT, stopOtherPreview);
    return () => {
      if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
      window.removeEventListener(VIDEO_PREVIEW_START_EVENT, stopOtherPreview);
    };
  }, [video.slug]);

  const startPreview = (mode: Exclude<PreviewMode, null>) => {
    if (!previewMode) resetPreviewAttempt();
    previewActivationRef.current = mode;
    window.dispatchEvent(
      new CustomEvent<string>(VIDEO_PREVIEW_START_EVENT, {
        detail: video.slug,
      })
    );
    setPreviewMode(mode);
  };

  const handlePointerEnter = () => {
    if (
      previewMode ||
      !window.matchMedia?.("(hover: hover) and (pointer: fine)").matches
    ) {
      return;
    }

    hoverTimerRef.current = setTimeout(() => {
      hoverTimerRef.current = null;
      startPreview("hover");
    }, HOVER_PREVIEW_DELAY_MS);
  };

  const handlePointerLeave = () => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
    if (previewMode === "hover") setPreviewMode(null);
  };

  const handlePreviewLoadedMetadata = () => {
    if (hasViewedRef.current) return;
    hasViewedRef.current = true;
    event("video_viewed", buildPreviewParams());
  };

  const handlePreviewPlay = () => {
    if (hasStartedRef.current) return;
    hasStartedRef.current = true;
    event("video_started", buildPreviewParams());
  };

  const handlePreviewTimeUpdate = (
    eventObject: SyntheticEvent<HTMLVideoElement>
  ) => {
    const player = eventObject.currentTarget;
    const previousMediaTime = lastMediaTimeRef.current;
    if (
      previousMediaTime != null &&
      !player.seeking &&
      !player.paused &&
      player.currentTime >= previousMediaTime
    ) {
      const delta = player.currentTime - previousMediaTime;
      if (delta <= 5) watchedSecondsRef.current += delta;
    }
    lastMediaTimeRef.current = player.currentTime;

    const duration = video.durationSeconds ?? player.duration;
    if (!Number.isFinite(duration) || duration <= 0) return;

    const progress = Math.min(100, (player.currentTime / duration) * 100);
    maxProgressRef.current = Math.max(maxProgressRef.current, progress);
    for (const milestone of VIDEO_PROGRESS_MILESTONES) {
      if (
        progress < milestone ||
        progressMilestonesRef.current.has(milestone)
      ) {
        continue;
      }
      progressMilestonesRef.current.add(milestone);
      event("video_progress", buildPreviewParams(milestone));
    }
  };

  const handlePreviewEnded = () => {
    if (hasCompletedRef.current) return;
    hasCompletedRef.current = true;
    maxProgressRef.current = 100;
    event("video_completed", buildPreviewParams(100));
  };

  const handlePreviewError = () => {
    if (hasErroredRef.current) return;
    hasErroredRef.current = true;
    event("video_error", {
      ...buildPreviewParams(),
      error_stage: "playback",
    });
  };

  const handlePreviewSeeking = (
    eventObject: SyntheticEvent<HTMLVideoElement>
  ) => {
    nativeSeekFromRef.current = lastMediaTimeRef.current;
    lastMediaTimeRef.current = eventObject.currentTarget.currentTime;
  };

  const handlePreviewSeeked = (
    eventObject: SyntheticEvent<HTMLVideoElement>
  ) => {
    const seekFromSeconds = nativeSeekFromRef.current;
    const seekToSeconds = eventObject.currentTarget.currentTime;
    nativeSeekFromRef.current = null;
    lastMediaTimeRef.current = seekToSeconds;
    if (seekFromSeconds == null || seekFromSeconds === seekToSeconds) return;

    event("video_seeked", {
      ...buildPreviewParams(),
      seek_method: "controls",
      seek_from_seconds: seekFromSeconds,
      seek_to_seconds: seekToSeconds,
      seek_direction: seekToSeconds >= seekFromSeconds ? "forward" : "backward",
    });
  };

  const trackingAttributes = {
    "data-ga-event": "content_click",
    "data-ga-category": "videos",
    "data-ga-action": "open_video_card",
    "data-ga-label": video.contentType,
    "data-ga-value": video.slug,
    "data-ga-value2": video.subject ?? undefined,
    "data-ga-source": "videos_hub",
    "data-ga-source-surface": "video_card",
    "data-ga-source-cluster": video.subject ?? undefined,
    "data-ga-target-type": "video_detail",
    "data-ga-target-id": video.slug,
    "data-ga-result-rank": video.resultRank,
  } as const;

  return (
    <article className="group min-w-0">
      <div
        className="relative aspect-[9/16] overflow-hidden rounded-xl bg-slate-950 shadow-sm ring-1 ring-slate-950/10 transition group-hover:shadow-lg group-hover:ring-blue-400 focus-within:ring-4 focus-within:ring-blue-200"
        onPointerEnter={handlePointerEnter}
        onPointerLeave={handlePointerLeave}
      >
        {previewMode ? (
          <>
            {/* Published videos include burned-in captions. The full watch page
                also provides the structured transcript and timestamp links. */}
            {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
            <video
              src={video.videoUrl}
              poster={video.posterUrl}
              controls
              autoPlay
              muted={previewMode === "hover"}
              playsInline
              preload="none"
              className="h-full w-full bg-slate-950 object-contain"
              aria-label={label("preview", `Preview ${video.fullTitle}`)}
              onLoadedMetadata={handlePreviewLoadedMetadata}
              onPlay={handlePreviewPlay}
              onTimeUpdate={handlePreviewTimeUpdate}
              onEnded={handlePreviewEnded}
              onError={handlePreviewError}
              onSeeking={handlePreviewSeeking}
              onSeeked={handlePreviewSeeked}
            />
            {previewMode === "hover" ? (
              <button
                type="button"
                onClick={() => startPreview("pinned")}
                className="absolute right-2 top-2 inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/95 text-blue-800 shadow-md transition hover:scale-105 hover:bg-white focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-blue-200"
                aria-label={label(
                  "sound",
                  `Play with sound and keep playing: ${video.fullTitle}`
                )}
              >
                <Play
                  className="ml-0.5 h-5 w-5 fill-current"
                  aria-hidden="true"
                />
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setPreviewMode(null)}
                className="absolute right-2 top-2 inline-flex h-10 w-10 items-center justify-center rounded-full bg-slate-950/85 text-white shadow-md transition hover:bg-slate-950 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-white/70"
                aria-label={label(
                  "close",
                  `Close preview of ${video.fullTitle}`
                )}
              >
                <X className="h-5 w-5" aria-hidden="true" />
              </button>
            )}
          </>
        ) : (
          <>
            <Link<string>
              href={video.href as Route<string>}
              prefetch={false}
              className="absolute inset-0 block focus-visible:outline-none"
              aria-label={label("open", `Open full video: ${video.fullTitle}`)}
              data-ga-impression="true"
              {...trackingAttributes}
            >
              <Image
                src={video.posterUrl}
                alt=""
                fill
                className="object-cover transition duration-500 group-hover:scale-[1.025]"
                sizes="(max-width: 639px) 45vw, (max-width: 1023px) 30vw, 22vw"
              />
              <div className="absolute inset-x-0 top-0 bg-gradient-to-b from-slate-950 via-slate-950/90 to-transparent px-3 pb-10 pt-3 sm:px-5 sm:pb-14 sm:pt-5">
                {video.topicLabel ? (
                  <div className="mb-2 flex items-baseline gap-1.5 text-blue-200">
                    <span className="text-[0.6rem] font-bold uppercase tracking-[0.16em] sm:text-xs">
                      {labels?.topic || "Topic"}
                    </span>
                    <span className="[font-family:var(--font-source-serif-4)] text-2xl font-semibold italic leading-none tracking-[-0.04em] sm:text-3xl">
                      {video.topicLabel}
                    </span>
                  </div>
                ) : null}
                <h3
                  className={`${
                    locale === "zh-Hans" ? "!leading-[1.35]" : ""
                  } line-clamp-4 max-w-[calc(100%-2.5rem)] text-balance [font-family:var(--font-source-serif-4)] text-[1.05rem] font-semibold leading-[1.03] tracking-[-0.02em] text-white drop-shadow-sm sm:max-w-[calc(100%-3.5rem)] sm:text-2xl sm:leading-[1.05] sm:tracking-[-0.025em]`}
                >
                  {video.thumbnailTitle}
                </h3>
              </div>
              <div className="absolute left-1/2 top-[57%] h-28 w-32 -translate-x-1/2 -translate-y-1/2 rounded-[2rem] bg-white/75 p-4 text-blue-950 shadow-sm ring-1 ring-white/80 backdrop-blur-[2px] sm:h-36 sm:w-40 sm:p-5">
                <RecognitionSketch kind={getMotifKind(video)} />
              </div>
            </Link>
            <button
              type="button"
              onClick={() => startPreview("pinned")}
              className="absolute right-2 top-2 inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/95 text-blue-800 shadow-md transition hover:scale-105 hover:bg-white focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-blue-200 sm:right-3 sm:top-3 sm:h-11 sm:w-11"
              aria-label={label("play", `Play preview of ${video.fullTitle}`)}
              data-ga-event="content_click"
              data-ga-category="videos"
              data-ga-action="request_inline_preview"
              data-ga-label={video.contentType}
              data-ga-value={video.slug}
              data-ga-value2={video.subject ?? undefined}
              data-ga-source="videos_hub"
              data-ga-source-surface="video_card"
              data-ga-source-cluster={video.subject ?? undefined}
              data-ga-target-type="video_preview"
              data-ga-target-id={video.slug}
              data-ga-result-rank={video.resultRank}
            >
              <Play
                className="ml-0.5 h-5 w-5 fill-current"
                aria-hidden="true"
              />
            </button>
            {video.duration ? (
              <span className="absolute bottom-3 right-3 rounded-md bg-slate-950/90 px-2 py-1 text-xs font-semibold tabular-nums text-white">
                {video.duration}
              </span>
            ) : null}
          </>
        )}
      </div>
    </article>
  );
}
